// Игра «Кто победит?» (§2.12) — замена старого опроса poll/. Рисуем по state сервера:
// open — кнопки (повторное нажатие на свой выбор снимает прогноз), locked — замок и доли, settled — итог и мои очки,
// void — матч не состоялся. Доли (percent) до своего голоса сервер не отдаёт (null) — это правило, не пробел.
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { usePredict, useGameFixture } from '../api/gameQueries';
import type { GameFixture } from '../api/gameTypes';
import type { MatchRow } from '../api/types';
import { t } from '../i18n/i18n';
import { paths } from '../nav';
import { haptic } from '../telegram/webapp';
import { Card } from './Card';
import { LiveGame } from './LiveGame';
import { MatchQuestions } from './MatchQuestions';
import { TeamLogo } from './TeamLogo';
import styles from './PredictionCard.module.css';

/** Блок для матча: сам грузит игру; нет матча в игре (404) или ошибка чтения — блока нет. Пока грузится — скелетон
 *  высотой блока (skeleton, Обзор матча), чтобы ответ не сдвигал всё ниже. */
export const GAME_ANCHOR = 'match-game';

/** livePollMs — частота экрана матча: у идущего матча блок с `live` перечитываем с ней (не чаще refresh_seconds).
 *  focus — пришли ссылкой matchgame_<id>: как только игра загрузилась, один раз прокручиваем к ней. */
export function PredictionCard({ fixtureId, match, path, livePollMs, focus, skeleton }: {
  fixtureId: number; match?: MatchRow; path?: string; livePollMs?: number; focus?: boolean; skeleton?: boolean;
}) {
  const navigate = useNavigate();
  const query = useGameFixture(fixtureId, path, true, livePollMs);
  const predict = usePredict(fixtureId);
  const fixture = query.data;
  const scrolled = useRef(false);
  const gone = useRef(false);
  const slot = gameSlot(query, gone.current);
  if (slot === 'gone') gone.current = true;
  useEffect(() => {
    if (!focus || !fixture || scrolled.current) return;
    scrolled.current = true;
    document.getElementById(GAME_ANCHOR)?.scrollIntoView({ block: 'start' });
  }, [focus, fixture]);
  if (slot === 'loading') return skeleton ? <GameSkeleton /> : null;
  if (slot !== 'game' || !fixture) return null;
  const subtitle = match ? [match.league?.name, match.round].filter(Boolean).join(' · ') : undefined;
  return (
    <Card title={t('tg_who_wins')} subtitle={subtitle || undefined} id={focus ? GAME_ANCHOR : undefined}
      onOpen={match ? () => navigate(paths.match(match.id)) : undefined}>
      <PredictionView fixture={fixture} pending={predict.isPending} failed={predict.isError}
        onPick={(pick) => { haptic.impact(); predict.mutate(pick); }} />
      <MatchQuestions fixture={fixture} />
      <LiveGame fixture={fixture} fixtureUrl={path} />
    </Card>
  );
}

type GameSlot = 'game' | 'loading' | 'none' | 'gone';

/** Что на месте игры: game — ответ есть; loading — ждём первый ответ; none — матча в игре нет (404); gone — игры нет,
 *  а первая попытка не удалась (5xx, таймаут, сеть) или запрос встал без сети. gone — защёлка до следующего открытия
 *  экрана: ответ повтора (shouldRetry, фокус) не вставляет игру посреди чтения, скелетон не возвращается и не мигает. */
export function gameSlot(query: { data: GameFixture | null | undefined; failureCount: number; isError: boolean; fetchStatus: string },
  gone: boolean): GameSlot {
  if (gone) return 'gone';
  if (query.data) return 'game';
  if (query.failureCount > 0 || query.isError || query.fetchStatus === 'paused') return 'gone';
  return query.data === null ? 'none' : 'loading';
}

/** Силуэт блока игры повторяет готовый блок матча до начала: заголовок, две кнопки исхода по 48, футер (у матча без
 *  голосов пустой — только отступ) и подсказка tg_game_hint. Подсказку рисуем той же строкой и типографикой (.hint),
 *  но прозрачной полоской: она переносится в те же строки на любом языке, ширине и кегле — ответ не сдвигает блоки
 *  ниже (HQ-1220). Числа — типичные значения scoring сервера (10 / 5 / 33), диктор их не читает. */
const SKELETON_SCORING = [10, 5, 33] as const;

function GameSkeleton() {
  return (
    <Card title={t('tg_who_wins')}>
      <div role="status" aria-busy="true" aria-label={t('tg_loading')}>
        <div className={styles.options}>
          <span className={styles.skOption} />
          <span className={styles.skOption} />
        </div>
        <div className={styles.footer} />
        <div className={styles.hint} aria-hidden="true">
          <span className={styles.skText}>{t('tg_game_hint', ...SKELETON_SCORING)}</span>
        </div>
      </div>
    </Card>
  );
}

/** Матч в списке вкладки «Игра»: блок приходит в ответе экрана; после голоса рисуем ответ PUT/DELETE.
 *  Вопросов к матчу в списке нет (дорого) — по кнопке грузим fixtures/{id}/ и рисуем их под исходом. */
export function GameFixtureItem({ fixture }: { fixture: GameFixture }) {
  const predict = usePredict(fixture.fixture_id, fixture.market);
  const [expanded, setExpanded] = useState(false);
  const full = useGameFixture(fixture.fixture_id, undefined, expanded);
  const shown = full.data ?? predict.data ?? fixture;
  // загрузили, а вопросов нет — у матча их нет: кнопку убираем
  const none = full.isSuccess && !full.data?.questions?.length;
  return (
    <>
      <PredictionView fixture={shown} pending={predict.isPending} failed={predict.isError} hideHint
        onPick={(pick) => { haptic.impact(); predict.mutate(pick); }} />
      {expanded && full.data && <MatchQuestions fixture={full.data} />}
      {!none && (
        <button className={styles.toggle} aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
          {expanded ? t('tg_game_q_hide') : t('tg_game_q_show')} {expanded ? '▴' : '▾'}
        </button>
      )}
    </>
  );
}

export function PredictionView({ fixture, pending, failed, onPick, hideHint }: {
  fixture: GameFixture; pending: boolean; failed: boolean; onPick: (pick: string | null) => void; hideHint?: boolean;
}) {
  const open = fixture.state === 'open';
  const logos: Record<string, GameFixture['home']> = { home: fixture.home, away: fixture.away };
  const scoring = fixture.scoring;
  return (
    <>
      <div className={styles.options}>
        {fixture.options.map((o) => {
          const mine = fixture.my_pick === o.pick;
          const winner = fixture.state === 'settled' && fixture.result?.winner === o.pick;
          const team = logos[o.pick];
          return (
            <button key={o.pick} className={`${styles.option} ${mine ? styles.mine : ''} ${winner ? styles.winner : ''}`}
              disabled={!open || pending} aria-pressed={mine}
              onClick={() => onPick(mine ? null : o.pick)}>
              {o.percent !== null && <span className={styles.fill} style={{ width: `${o.percent}%` }} />}
              {team && <TeamLogo src={team.image} code={team.code} size={28} />}
              <span className={styles.name}>{o.label}</span>
              {o.percent !== null && <span className="score">{Math.round(o.percent)}%</span>}
            </button>
          );
        })}
      </div>
      {fixture.note && <div className={styles.note}>{fixture.note}</div>}
      <Outcome fixture={fixture} />
      <div className={styles.footer}>
        {fixture.total_votes > 0 && <span>{fixture.total_votes === 1 ? t('tg_votes_one') : t('tg_votes', fixture.total_votes)}</span>}
        {fixture.state === 'locked' && <span>🔒 {t('tg_vote_closed')}</span>}
        {open && !fixture.my_pick && fixture.total_votes > 0 && <span>{t('tg_game_see_votes')}</span>}
        {open && fixture.my_pick && <span>{t('tg_game_withdraw')}</span>}
        {failed && <span className={styles.error}>{t('mc_error_title')}</span>}
      </div>
      {open && scoring && !hideHint && (
        <div className={styles.hint}>{t('tg_game_hint', scoring.correct, scoring.underdog_bonus, scoring.underdog_share_percent)}</div>
      )}
    </>
  );
}

function Outcome({ fixture }: { fixture: GameFixture }) {
  if (fixture.state === 'void') return <div className={styles.outcome}>{t('tg_game_void')}</div>;
  const r = fixture.my_result;
  if (fixture.state !== 'settled' || !r) return null;
  if (r.status === 'won') return <div className={`${styles.outcome} ${styles.won}`}>{t('tg_game_won', r.points)}</div>;
  if (r.status === 'lost') return <div className={styles.outcome}>{t('tg_game_lost')}</div>;
  if (r.status === 'void') return <div className={styles.outcome}>{t('tg_game_void')}</div>;
  return null;
}
